/*---------------------------------------------------------------------------------------------
* Copyright (c) Microsoft Corporation. All rights reserved.
* Licensed under the MIT License. See License.txt in the project root for license information.
*--------------------------------------------------------------------------------------------*/
import {generateImageUrl} from '@msdyn365-commerce-modules/retail-actions';
import {
    Button as CoreButton,
    getPayloadObject,
    getTelemetryAttributes,
    getTelemetryObject,
    IPayLoad,
    ITelemetryContent,
    Module,
    Node,
    onTelemetryClick
} from '@msdyn365-commerce-modules/utilities';
import MsDyn365, {IImageData, IImageSettings, Image} from '@msdyn365-commerce/core';
import classnames from 'classnames';
import * as React from 'react';
import {NavMenuStateInput} from '../../data-actions/navigation-menu-state-data-action';
import { isExternalLink } from '../../Utilities/isExternalLink';
import {navLinkKeywordRemoval} from '../../Utilities/plp-url-utils';
import {INavigationMenuViewProps} from './mfrm-navigation-menu';
import {IMenuItemData} from './mfrm-navigation-menu.data';

interface INavigationState {
    parentMenu?: number;
    activeMenu?: number[];
    activeMenuLabels?: string[];
    categoryImage?: string | null;
    mobileViewLabelText?: string;
    showModal?: boolean;
    dataShared: boolean;
}

/**
 *
 * MfrmNavigationMenuView component
 * @extends {React.PureComponent<INavigationMenuViewProps>}
 */
export class MfrmNavigationMenuView extends React.PureComponent<INavigationMenuViewProps, INavigationState> {
    // VSI-Customization - start - 10/12/2020
    private activeIndex: number = 0;
    private currentSubMenu: JSX.Element[] = [];
    private possibleParentMenus: IMenuItemData[] = [];
    private parentMenu: IMenuItemData | undefined;
    private mobileRoot: IMenuItemData | undefined;
    // VSI-Customization - end
    private currentLevel: number = 0;
    private menuNode: React.RefObject<HTMLUListElement>;
    private telemetryContent: ITelemetryContent;
    private payLoad: IPayLoad;
    constructor(props: INavigationMenuViewProps) {
        super(props);
        this.menuNode = React.createRef();
        this.state = { activeMenu: undefined, activeMenuLabels: undefined, mobileViewLabelText: '', parentMenu: undefined, showModal: false, dataShared: false };
        this._closeSubmenu = this._closeSubmenu.bind(this);
        this.telemetryContent = getTelemetryObject(this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry);
        this.payLoad = getPayloadObject('click', this.telemetryContent, '', '');
    }

    public componentDidUpdate(): void {
        const { menuItemData } = this.props;
        if (this.props.data.navMenuState.result && !this.state.dataShared) {
            const navMenuState = this.props.data.navMenuState?.result;
            this.props.context.actionContext.update(new NavMenuStateInput(), { ...navMenuState, menuItemData: menuItemData });
            this.setState({ dataShared: true });
        }
        if (MsDyn365.isBrowser && this.isWmap()) {
            const collection = document.getElementsByTagName('a');
            if (collection.length) {
                for (let i = 0; i < collection.length; i++) {
                    const href = collection[i].getAttribute('href')?.replace(`/${this.props.app.config.wmapPageUrlSuffix}`, '');
                    collection[i].setAttribute('href', href!);
                }
            }
        }
    }

    public render(): JSX.Element | null {
        const { isMobileView, MenuList, MobileDescriptionContainer, MobileDescriptionLabel, Navigation } = this.props;
        this.currentLevel = 1;
        this.activeIndex = 0;
        this.currentSubMenu = [];
        this.possibleParentMenus = [];
        this.parentMenu = undefined;

        const mobileCollapsedState = this.props.data.navMenuState.result?.isMobileMenuCollapsed;
        if (mobileCollapsedState && isMobileView) {
            this.setState({ activeMenu: undefined, activeMenuLabels: undefined });
        }

        const query = this.props.context.request.query;

        const linkList = <Node className='nav-menu-link-items'>{this._displayMenuItemData()}</Node>;

        return (
            <>
                {query && query.hasOwnProperty('emitCategories') && query.emitCategories === 'true' && (
                    <Node className='emitCategories' id='emitCategories'>
                        <script type='application:json'>{JSON.stringify(this.props.menuItemData)}</script>
                        <Node className='json' style={{ display: 'none' }}>{JSON.stringify(this.props.menuItemData)}</Node>
                    </Node>
                )}
                <Module {...Navigation} className={classnames(Navigation.className, isMobileView && this.state.activeMenu !== undefined ? 'child' : 'parent')}>
                    {/* menu list creates an ul with role menu */}
                    <Node {...MenuList} ref={this.menuNode} tabIndex='-1'>
                        {/* active menu is undefined by default */}
                        {isMobileView && this.state.activeMenu !== undefined &&
                            <Node {...MobileDescriptionContainer}>
                                <Node {...MobileDescriptionLabel}>{this.state.mobileViewLabelText}</Node>
                            </Node>}
                        {this._renderDisplay()}
                    </Node>
                    {/* modal for desktop */}
                    {!isMobileView &&
                        <div className={`${Navigation.className}_modal ${this.state.showModal && 'show'}`} />
                    }
                </Module>
                {/* Render seo link-list in the page source. */}
                {linkList}
            </>
        );
    }

    private isWmap = (): boolean => {
        const pathName = this.props.context.request && this.props.context.request.url.requestUrl.pathname?.toLowerCase().split('/');
        const makeaPaymentLink = this.props.context?.app.config.wmapPageUrlSuffix?.toLowerCase();
        return pathName && makeaPaymentLink && pathName?.includes(makeaPaymentLink);
    };
    private _renderDisplay(): JSX.Element[] {
        const { ListItem, menuItemData, isMobileView } = this.props;
        const menuItemList: JSX.Element[] = [];

        // VSI-Customization - start - Mouse Leave Event
        menuItemData.forEach((item: IMenuItemData, index: number) => {
            menuItemList.push(
                <Node key={index} {...ListItem} onMouseLeave={!isMobileView && this._closeSubmenu}>
                    {this._createMenuItemList(item)}
                </Node>
                // VSI-Customization - end
            );
        });
        return menuItemList;
    }

    private _displayMenuItemData(): JSX.Element[] {
        const { menuItemData } = this.props;
        const allMenuItemArray = [];

        for (let index = 0; index < menuItemData.length; index++) {
            const currentMenu = menuItemData[index];

            if (currentMenu.subMenu) {
                for (let subMenuIndex = 0; subMenuIndex < currentMenu.subMenu.length; subMenuIndex++) {
                    const subMenu = currentMenu.subMenu[subMenuIndex];

                    if (subMenu.subMenu) {
                        for (let nestedSubMenuIndex = 0; nestedSubMenuIndex < subMenu.subMenu.length; nestedSubMenuIndex++) {
                            const nestedSubMenu = subMenu.subMenu[nestedSubMenuIndex];

                            if (nestedSubMenu.subMenu) {
                                for (let doublyNestedSubMenuIndex = 0; doublyNestedSubMenuIndex < nestedSubMenu.subMenu.length; doublyNestedSubMenuIndex++) {
                                    const doublyNestedSubMenu = nestedSubMenu.subMenu[doublyNestedSubMenuIndex];
                                    allMenuItemArray.push(doublyNestedSubMenu);
                                }
                            }
                            allMenuItemArray.push(nestedSubMenu);
                        }
                    }
                    allMenuItemArray.push(subMenu);
                }
            }
            allMenuItemArray.push(currentMenu);
        }

        // Filter out empty href links
        const MenuItems = allMenuItemArray.filter(({ linkURL }) => !!linkURL);

        // Build anchor node from array of menu items
        return MenuItems.map(({id, linkText, linkURL}) => <a key={id} href={linkURL}>{linkText}</a>);
    }

    private _createMenuItemList(menuItemData: IMenuItemData): JSX.Element | null {
        if (menuItemData && menuItemData.subMenu && menuItemData.subMenu.length > 0) {
            if (this.props.isMobileView && !this.state.activeMenu) {
                return this._renderButtonMenuItem(menuItemData, 0);
            } else if (this.props.isMobileView) {
                return this._renderSubMenu(menuItemData.subMenu, menuItemData.id, false);
            } else {
                return (
                    <React.Fragment>
                        {this._renderButtonMenuItem(menuItemData)}
                        {this._renderSubMenu(menuItemData.subMenu, menuItemData.id, false)}
                    </React.Fragment>
                );
            }
        } else if (menuItemData && menuItemData.linkText && menuItemData.linkURL && menuItemData.linkURL.length > 0) {
            return this.props.isMobileView ? this.state.activeMenu?.length ? null : this._renderLinkMenuItem(menuItemData, menuItemData.id, false) : this._renderLinkMenuItem(menuItemData, menuItemData.id, false);
        } else if (menuItemData && menuItemData.linkText && !menuItemData.linkURL) {
            return this.props.isMobileView ? this.state.activeMenu?.length ? null : this._renderSpanMenuItem(menuItemData) : this._renderSpanMenuItem(menuItemData);
        }
        this.props.context.telemetry.warning('Navigation menu content is empty, module wont render.');
        return null;
    }

    // tslint:disable-next-line: cyclomatic-complexity
    private _renderSubMenu(subMenus?: IMenuItemData[], activeMenu?: number, IsSubMenu?: boolean): JSX.Element | null {
        const { ListItem } = this.props;
        // VSI-Customization - start
        const enableMultiSupportMenu = this.props.config.enableMultilevelMenu || false;
        // default of 4 is set as requested by MFRM
        const multiLevelSupportedMenu = this.props.config.menuLevelSupport || 4;

        if (activeMenu && (!this.state.activeMenu || (this.state.activeMenu[this.activeIndex]) !== activeMenu)) {
            this.props.context.telemetry.warning('Navigation Active menu content is empty, module wont render.');
            this.activeIndex > 0 && this.activeIndex--;
            this.currentLevel > 1 && this.currentLevel--;
            return null;
        }

        if (!subMenus || subMenus.length === 0) {
            this.props.context.telemetry.warning('Navigation Submenu content is empty, module wont render.');
            this.activeIndex > 0 && this.activeIndex--;
            this.currentLevel > 1 && this.currentLevel--;
            return null;
        }

        let levelClassName: string = '';
        const menuItems = [];

        const isLastSubMenu = this.state.activeMenu && this.activeIndex === this.state.activeMenu.length - 1;
        const isLastParent = this.state.activeMenu && this.activeIndex === this.state.activeMenu.length - 2;

        for (let idx = 0; idx < subMenus.length; idx++) {
            const option: IMenuItemData = subMenus[idx];
            const hasOptions = (option.subMenu && option.subMenu.length > 0);
            const haveSubmenu = hasOptions && enableMultiSupportMenu && (this.currentLevel <= Math.round(multiLevelSupportedMenu) - 1);

            let menuItem: JSX.Element | null;
            if (haveSubmenu) {
                menuItem = this._renderButtonMenuItem(option, activeMenu, idx);
            } else {
                menuItem = (option.linkURL
                    ? this._renderLinkMenuItem(option, idx, false)
                    : this._renderSpanMenuItem(option)
                );
            }

            // reached active submenu so build a list of its children to display
            if (isLastSubMenu) {
                this.currentSubMenu.push(
                    <Node {...ListItem} key={option.id}>
                        {menuItem}
                    </Node>
                );

                // get the parent of this submenu
                this.parentMenu = this.possibleParentMenus.find(menu => menu.id === activeMenu);

                continue;
                // reached parent of active submenu so set this.activeSubMenuParent here
            } else if (isLastParent) {
                this.possibleParentMenus.push(option);
            }

            // no active submenu found so keep looking
            if (haveSubmenu) {
                this.currentLevel++;
                this.activeIndex++;
                levelClassName = enableMultiSupportMenu ? `level-${this.currentLevel.toString()}` : '';
                this._renderSubMenu(option.subMenu, option.id, true);
            }

            menuItems.push(
                <Node {...ListItem} key={option.id} className={classnames(ListItem.className, haveSubmenu && 'havesubmenu')}>
                    {menuItem}
                </Node>
            );
        }

        this.activeIndex--;
        return this.currentSubMenu.length ? (this._renderMenu(levelClassName, this.currentSubMenu, activeMenu, IsSubMenu)) : (this._renderMenu(levelClassName, menuItems, activeMenu, IsSubMenu));
        // VSI-Customization - end

    }

    private _renderButtonMenuItem(option: IMenuItemData, activeMenu?: number, index?: number): JSX.Element | null {
        const { Button, isMobileView } = this.props;
        const isRoot = !this.state.parentMenu && !activeMenu;
        const attributes = getTelemetryAttributes(this.telemetryContent, this.payLoad);
        const attribute = isRoot && !this.props.isMobileView ?
            { onMouseOver: this._handleDropdownToggle(option, activeMenu), onClick: this._handleOnClick(option, activeMenu), onFocus: this._handleDropdownToggle(option, activeMenu) }
            :
            { onClick: this._handleDropdownToggle(option, activeMenu) };
        const menuItemColor = isMobileView ? option?.menuItemColorMobile : option?.menuItemColorDesktop;
        return (
            <Node
                key={index}
                {...Button}
                // VSI-Customization - start
                {...attribute}
                style={{ color: menuItemColor || '' }}
                data-cy={option.linkText}
                {...attributes}
                // VSI-Customization - end
                aria-haspopup={true}
                aria-expanded={this.state.activeMenu && this.state.activeMenu[this.activeIndex] === option.id}
                data-parent={activeMenu}
            >
                {option.linkText}
            </Node>
        );
    }

    private _renderLinkMenuItem(option: IMenuItemData, index?: number, hoverEffect: boolean = true): JSX.Element | null {
        const { Link, isMobileView } = this.props;
        const linkText = option.linkText ? option.linkText : '';
        const imagesource = option.imageSource ? option.imageSource : '';
        this.payLoad.contentAction.etext = linkText;
        const attributes = getTelemetryAttributes(this.telemetryContent, this.payLoad);
        const menuItemColor = isMobileView ? option?.menuItemColorMobile : option?.menuItemColorDesktop;
        return (
            // VSI Customization - start - Added inline style for menu item color
            <Node
                {...Link}
                key={index}
                style={{ color: menuItemColor || '' }}
                data-cy={option.linkText}
                href={option.linkURL}
                {...attributes}
                onMouseOver={hoverEffect ? this._updateCategoryImage(imagesource) : () => null}
                // tslint:disable-next-line: jsx-no-lambda react-this-binding-issue
                onClick={(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => { this._handleOnClickMenuItem(event, option); }}
            >
                {option.linkText}
            </Node>
            // VSI Customization - end
        );
    }

    private _handleOnClickMenuItem(event: React.MouseEvent<HTMLButtonElement, MouseEvent>, option: IMenuItemData): void {
        const telemetryContent = getTelemetryObject('Navigation Header', this.props.friendlyName, this.props.telemetry);
        const payLoad = getPayloadObject('click', telemetryContent, 'Nav', '');
        // @ts-ignore
        payLoad.contentAction.linkText = option.linkText;
        onTelemetryClick(telemetryContent, payLoad, 'Nav')();
        // @ts-ignore
        if (utag) {
            event.preventDefault();
            const isExternal = isExternalLink(option.linkURL);
            const eventName = isExternal ? 'outbound-general-link-click' : 'internal-navigation';
            const eventCategory = isExternal ? 'outbound links' : 'internal links';
            const internalDestinationUrl = option.linkURL === '/' ? '/' : option.linkURL!;
            const internalLocalUrl = MsDyn365.isBrowser && (window.location.href === '/' ? '/' : window.location.href.split(location.host)[1].split('?')[0]);
            const eventAction = isExternal ? `to: ${option.linkURL}` : `to: ${internalDestinationUrl}`;
            const eventLabel = isExternal ? `from: ${MsDyn365.isBrowser && window.location.href}` : `from header: ${this.state.activeMenuLabels?.join(' > ')}: ${internalLocalUrl}`;

            // @ts-ignore
            utag.link({ event: eventName, event_category: eventCategory, event_action: eventAction, event_label: eventLabel, event_noninteraction: 'false' });
            if (MsDyn365.isBrowser) {
                const isOpenInNewTab = option.openInNewTab;
                !isOpenInNewTab ? window.location.href = option.linkURL! : window.open(option.linkURL);
            }
        }
    }

    private _renderSpanMenuItem(option: IMenuItemData, index?: number): JSX.Element | null {
        const { Span, isMobileView } = this.props;
        const menuItemColor = isMobileView ? option?.menuItemColorMobile : option?.menuItemColorDesktop;
        return (
            // VSI Customization - start - Added inline style for menu item color
            <Node key={index} {...Span} style={{ color: menuItemColor || '' }} onClick={this._handleDropdownToggle(option, index)}>{option.linkText}</Node>
            // VSI Customization - end
        );
    }

    // tslint:disable-next-line: max-func-body-length cyclomatic-complexity
    private _renderMenu(level: string, menuOptions: JSX.Element[], currentItem?: number, submenu?: boolean): JSX.Element | null {
        const { MenuList, MobileDescriptionContainer, MobileBackButton, ImageDivContainer, showCategoryImage, isMobileView } = this.props;
        const categoryImageDisplay = !isMobileView && showCategoryImage && this.state.categoryImage !== null && this.state.categoryImage !== undefined && !submenu;
        this.currentLevel = 1;
        const showSubMenuHeader = this.props.isMobileView || (!this.props.isMobileView && this.state.parentMenu !== undefined);
        const showRootData = this.props.isMobileView && this.state.activeMenu && this.state.activeMenu.length === 1;
        const attributes = getTelemetryAttributes(this.telemetryContent, this.payLoad);

        return (
            <Node {...MenuList} className={classnames(MenuList.className, level)} {...attributes}>
                {showRootData ?
                    <Node {...MobileDescriptionContainer}>
                        <Node {...MobileBackButton} onClick={this._handleGoBack()} >{this.props.resources.backButtonAriaLabel}</Node>
                        <Node {...{ className: 'ms-nav__menu-wrapper' }}>
                            {this.mobileRoot?.linkText &&
                                <CoreButton
                                    className='ms-nav__menu-title'
                                    data-cy={this.mobileRoot?.linkText}
                                    href={this.mobileRoot?.linkURL ? this.mobileRoot?.linkURL : 'javascript:void(0);'}
                                    onClick={(event) => {
                                        const telemetryContent = getTelemetryObject(
                                            'Navigation Header',
                                            this.props.friendlyName,
                                            this.props.telemetry
                                        );
                                        const payLoad = getPayloadObject('click', telemetryContent, 'Nav', '');
                                             // @ts-ignore
                                             payLoad.contentAction.linkText = this.mobileRoot?.linkText;
                                            onTelemetryClick(telemetryContent, payLoad, 'Nav')();
                                        // @ts-ignore
                                        if (utag) {
                                            // tslint:disable-next-line: jsx-no-lambda react-this-binding-issue
                                            event.preventDefault();
                                            const isExternal = isExternalLink(this.mobileRoot!.linkURL);
                                            const eventName = isExternal ? 'outbound-general-link-click' : 'internal-navigation';
                                            const eventCategory = isExternal ? 'outbound links' : 'internal links';
                                            const internalDestinationUrl = this.mobileRoot!.linkURL! === '/' ? '/' : this.mobileRoot!.linkURL!;
                                            const internalLocalUrl = MsDyn365.isBrowser && (window.location.href === '/' ? '/' : window.location.href.split(location.host)[1].split('?')[0]);
                                            const eventAction = isExternal ? `to: ${this.mobileRoot!.linkURL!}` : `to: ${internalDestinationUrl}`;
                                            const eventLabel = isExternal ? `from: ${MsDyn365.isBrowser && window.location.href}` : `from header: ${this.state.activeMenuLabels?.join(' > ')}: ${internalLocalUrl}`;
                                            const isOpenInNewTab = this.mobileRoot?.openInNewTab;
                                            // @ts-ignore
                                            utag.link({ event: eventName, event_category: eventCategory, event_action: eventAction, event_label: eventLabel, event_noninteraction: 'false' });
                                            if (MsDyn365.isBrowser) {
                                                // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
                                                !isOpenInNewTab ? this.mobileRoot?.linkURL ? window.location.href = this.mobileRoot?.linkURL : null : window.open(this.mobileRoot?.linkURL!);
                                            }
                                        }
                                    }
                                    }
                                >
                                    {this.mobileRoot.linkText}
                                </CoreButton>}
                            {this.mobileRoot?.clpLinkText && this.mobileRoot.clpLink?.destinationUrl &&
                                <CoreButton
                                    className='ms-nav__menu-clp-link'
                                    data-cy={this.mobileRoot?.clpLinkText}
                                    href={navLinkKeywordRemoval(this.mobileRoot.clpLink?.destinationUrl, this.props.context.request)}
                                    onClick={(event) => {
                                        const telemetryContent = getTelemetryObject(
                                            'Navigation Header',
                                            this.props.friendlyName,
                                            this.props.telemetry
                                        );
                                        const payLoad = getPayloadObject('click', telemetryContent, 'Nav', '');
                                        // @ts-ignore
                                        payLoad.contentAction.linkText = this.mobileRoot?.clpLinkText;
                                        onTelemetryClick(telemetryContent, payLoad, 'Nav')();
                                        // @ts-ignore
                                        if (utag) {
                                            // tslint:disable-next-line: jsx-no-lambda react-this-binding-issue
                                            event.preventDefault();
                                            const clpMobileRootLink = navLinkKeywordRemoval(this.mobileRoot!.clpLink?.destinationUrl || '', this.props.context.request);
                                            const isExternal = isExternalLink(this.mobileRoot!.clpLink?.destinationUrl);
                                            const eventName = isExternal ? 'outbound-general-link-click' : 'internal-navigation';
                                            const eventCategory = isExternal ? 'outbound links' : 'internal links';
                                            const internalDestinationUrl = clpMobileRootLink === '/' ? '/' : clpMobileRootLink;
                                            const internalLocalUrl = MsDyn365.isBrowser && (window.location.href === '/' ? '/' : window.location.href.split(location.host)[1].split('?')[0]);
                                            const eventAction = isExternal ? `to: ${clpMobileRootLink}` : `to: ${internalDestinationUrl}`;
                                            const eventLabel = isExternal ? `from: ${MsDyn365.isBrowser && window.location.href}` : `from header: ${this.state.activeMenuLabels?.join(' > ')}: ${internalLocalUrl}`;
                                            const isOpenInNewTab = this.mobileRoot?.openInNewTab;
                                            // @ts-ignore
                                            utag.link({ event: eventName, event_category: eventCategory, event_action: eventAction, event_label: eventLabel, event_noninteraction: 'false' });
                                            if (MsDyn365.isBrowser) {
                                                !isOpenInNewTab ? window.location.href = clpMobileRootLink : window.open(clpMobileRootLink);
                                            }
                                        }
                                    }
                                    }
                                >
                                    {this.mobileRoot.clpLinkText}
                                </CoreButton>}
                        </Node>
                    </Node> :
                    showSubMenuHeader &&
                    <Node {...MobileDescriptionContainer}>
                        <Node {...MobileBackButton} onClick={this._handleGoBack()} >{this.props.resources.backButtonAriaLabel}</Node>
                        <Node {...{ className: 'ms-nav__menu-wrapper' }} {...attributes}>
                            {this.parentMenu?.linkText &&
                                <CoreButton
                                    className='ms-nav__menu-title'
                                    data-cy={this.parentMenu?.linkText}
                                    href={this.parentMenu.linkURL}
                                    // tslint:disable-next-line: jsx-no-lambda react-this-binding-issue
                                    onClick={(event) => {
                                        const telemetryContent = getTelemetryObject(
                                            'Navigation Header',
                                            this.props.friendlyName,
                                            this.props.telemetry
                                        );
                                        const payLoad = getPayloadObject('click', telemetryContent, 'Nav', '');
                                        // @ts-ignore
                                        payLoad.contentAction.linkText = this.parentMenu?.linkText;
                                        onTelemetryClick(telemetryContent, payLoad, 'Nav')();
                                        // @ts-ignore
                                        if (utag) {
                                            event.preventDefault();
                                            const isExternal = isExternalLink(this.parentMenu?.linkURL);
                                            const eventName = isExternal ? 'outbound-general-link-click' : 'internal-navigation';
                                            const eventCategory = isExternal ? 'outbound links' : 'internal links';
                                            const internalDestinationUrl = this.parentMenu!.linkURL! === '/' ? '/' : this.parentMenu!.linkURL!;
                                            const internalLocalUrl = MsDyn365.isBrowser && (window.location.href === '/' ? '/' : window.location.href.split(location.host)[1].split('?')[0]);
                                            const eventAction = isExternal ? `to: ${this.parentMenu!.linkURL!}` : `to: ${internalDestinationUrl}`;
                                            const eventLabel = isExternal ? `from: ${MsDyn365.isBrowser && window.location.href}` : `from header: ${this.state.activeMenuLabels?.join(' > ')}: ${internalLocalUrl}`;
                                            const isOpenInNewTab = this.mobileRoot?.openInNewTab;
                                            // @ts-ignore
                                            utag.link({ event: eventName, event_category: eventCategory, event_action: eventAction, event_label: eventLabel, event_noninteraction: 'false' });
                                            if (MsDyn365.isBrowser) {
                                                !isOpenInNewTab ? window.location.href = this.parentMenu!.linkURL! : window.open(this.parentMenu!.linkURL);
                                            }
                                        }
                                    }
                                    }
                                >
                                    {this.parentMenu.linkText}
                                </CoreButton>}
                            {this.parentMenu?.clpLinkText && this.parentMenu.clpLink?.destinationUrl &&
                                <CoreButton
                                    className='ms-nav__menu-clp-link'
                                    data-cy={this.parentMenu?.clpLinkText}
                                    href={navLinkKeywordRemoval(this.parentMenu.clpLink?.destinationUrl, this.props.context.request)}
                                    // tslint:disable-next-line: jsx-no-lambda react-this-binding-issue
                                    onClick={(event) => {
                                        const telemetryContent = getTelemetryObject(
                                            'Navigation Header',
                                            this.props.friendlyName,
                                            this.props.telemetry
                                        );
                                        const payLoad = getPayloadObject('click', telemetryContent, 'Nav', '');
                                        // @ts-ignore
                                        payLoad.contentAction.linkText = this.parentMenu?.clpLinkText;
                                        onTelemetryClick(telemetryContent, payLoad, 'Nav')();
                                        // @ts-ignore
                                        if (utag) {
                                            event.preventDefault();
                                            const clpParentMenuLink = navLinkKeywordRemoval(this.parentMenu!.clpLink?.destinationUrl || '', this.props.context.request);
                                            const isExternal = isExternalLink(this.parentMenu?.clpLink?.destinationUrl);
                                            const eventName = isExternal ? 'outbound-general-link-click' : 'internal-navigation';
                                            const eventCategory = isExternal ? 'outbound links' : 'internal links';
                                            const internalDestinationUrl = clpParentMenuLink === '/' ? '/' : clpParentMenuLink;
                                            const internalLocalUrl = MsDyn365.isBrowser && (window.location.href === '/' ? '/' : window.location.href.split(location.host)[1].split('?')[0]);
                                            const eventAction = isExternal ? `to: ${clpParentMenuLink}` : `to: ${internalDestinationUrl}`;
                                            const eventLabel = isExternal ? `from: ${MsDyn365.isBrowser && window.location.href}` : `from header: ${this.state.activeMenuLabels?.join(' > ')}: ${internalLocalUrl}`;
                                            const isOpenInNewTab = this.mobileRoot?.openInNewTab;
                                            // @ts-ignore
                                            utag.link({ event: eventName, event_category: eventCategory, event_action: eventAction, event_label: eventLabel, event_noninteraction: 'false' });
                                            if (MsDyn365.isBrowser) {
                                                !isOpenInNewTab ? window.location.href = clpParentMenuLink : window.open(clpParentMenuLink);
                                            }
                                        }
                                    }
                                    }
                                >
                                    {this.parentMenu.clpLinkText}
                                </CoreButton>}
                        </Node>
                    </Node>}
                {menuOptions}
                {
                    categoryImageDisplay && this.state.categoryImage && (
                        <Node {...ImageDivContainer} className={ImageDivContainer!.className}>
                            {this.state.categoryImage && this._getCategoryImage(this.state.categoryImage)}
                        </Node>
                    )
                }
            </Node>
        );
    }

    private _updateCategoryImage = (categoryImageSrc: string) => () => {
        this.setState({
            categoryImage: categoryImageSrc !== '' ? categoryImageSrc : 'empty'
        });
    };

    private _handleOnClick = (data: IMenuItemData, parentId?: number) => () => {
        const telemetryContent = getTelemetryObject('Navigation Header', this.props.friendlyName, this.props.telemetry);
        const payLoad = getPayloadObject('click', telemetryContent, 'Nav', '');
        // @ts-ignore
        onTelemetryClick(telemetryContent, payLoad, 'Nav')();
        if (MsDyn365.isBrowser) {
            if (!parentId && data.linkURL) {
                window.location.href = data.linkURL;
            }
        }
    };

    private _handleDropdownToggle = (data: IMenuItemData, parentId?: number) => () => {
        // VSI Customization - start
        let updatedActiveMenu;
        let updatedActiveMenuLabels;
        if (this.state.activeMenu) {
            if (this.state.activeMenu[this.state.activeMenu.length - 1] !== data.id) {
                updatedActiveMenu = data.id && this.state.activeMenu.concat(data.id);
                updatedActiveMenuLabels = data.linkText && this.state.activeMenuLabels!.concat(data.linkText);
            } else {
                updatedActiveMenu = this.state.activeMenu;
                updatedActiveMenuLabels = this.state.activeMenuLabels;
            }
        } else {
            updatedActiveMenu = data.id && [data.id];
            updatedActiveMenuLabels = data.linkText && [data.linkText];
        }
        if (typeof (updatedActiveMenu) === 'number') { updatedActiveMenu = [updatedActiveMenu]; }
        if (typeof (updatedActiveMenuLabels) === 'string') { updatedActiveMenuLabels = [updatedActiveMenuLabels]; }

        // set root of mobile menu to display above submenu
        if (this.props.isMobileView && (!this.state.activeMenu || !this.state.activeMenu.length)) {
            this.mobileRoot = data;
            // toggle mobile links
            this._toggleMobileLinks(true);
        }

        // show modal
        const showModal = !!(updatedActiveMenu && updatedActiveMenu.length);

        updatedActiveMenu && this.setState({
            activeMenu: updatedActiveMenu,
            activeMenuLabels: updatedActiveMenuLabels,
            parentMenu: parentId,
            showModal: showModal
        });

        if (this.props.showCategoryImage) {
            this._updateCategoryImage(data.imageSource!)();
        }

        // VSI Customization - end
        this._resetFocus();
    };

    private _handleGoBack = () => () => {
        // VSI Customization - start
        const updatedActiveMenu = this.state.activeMenu;
        const updatedActiveMenuLabels = this.state.activeMenuLabels;
        const finalUpdate = updatedActiveMenu && updatedActiveMenu.length > 1 ? updatedActiveMenu.slice(0, updatedActiveMenu.length - 1) : undefined;
        const finalUpdateMenuLabels = updatedActiveMenuLabels && updatedActiveMenuLabels.length > 1 ? updatedActiveMenuLabels.slice(0, updatedActiveMenuLabels.length - 1) : undefined;

        // toggle mobile links
        !finalUpdate && this._toggleMobileLinks(false);

        // parent set to undefined when on the root menu so back button doesn't show up
        const newParentMenu = this.state.activeMenu && this.state.activeMenu.length === 2;
        newParentMenu ? this.setState({ activeMenu: finalUpdate, activeMenuLabels: finalUpdateMenuLabels, parentMenu: undefined }) : this.setState({ activeMenu: finalUpdate, activeMenuLabels: finalUpdateMenuLabels });

        // VSI Customization - end
        this._resetFocus();
    };

    private _resetFocus = () => {
        if (this.props.isMobileView) {
            MsDyn365.isBrowser && setTimeout(() => {
                this.menuNode && this.menuNode.current && this.menuNode.current.focus();
            }, 0);
        }
    };

    private _toggleMobileLinks = (result: boolean): void => {
        const navMenuState = this.props.data.navMenuState?.result;
        if (navMenuState) {
            // This will directly update our application state, which should trigger all modules observing the state to update
            this.props.context.actionContext.update(new NavMenuStateInput(), { ...navMenuState, isMenuHasParent: result });
        }
    };

    private _closeSubmenu(): void {
        // VSI Customization - start
        this.setState({ activeMenu: undefined, activeMenuLabels: undefined, mobileViewLabelText: '', parentMenu: undefined, showModal: false, categoryImage: null });
        // VSI Customization - end
    }

    private _getCategoryImage = (categoryImage: string): React.ReactNode | null => {
        const categoryImageUrl = generateImageUrl(`${categoryImage}`, this.props.context.actionContext.requestContext.apiSettings);
        const defaultImageSettings: IImageSettings = {
            viewports: {
                xs: { q: 'w=300&h=250&m=8', w: 0, h: 0 },
                sm: { q: 'w=300&h=250&m=8', w: 0, h: 0 },
                md: { q: 'w=300&h=250&m=8', w: 0, h: 0 },
                lg: { q: 'w=300&h=250&m=8', w: 0, h: 0 }
            },
            lazyload: true
        };
        if (categoryImageUrl !== undefined) {
            const ImageData: IImageData = { src: categoryImageUrl };
            return (
                <Image
                    className='ms-nav-image__item'
                    {...ImageData}
                    gridSettings={this.props.context.request.gridSettings!}
                    imageSettings={this.props.config.categoryImageSettings || defaultImageSettings}
                    loadFailureBehavior='hide'
                    role='tabpanel'
                    id={`categoryImageTag__categoryImage`}
                />
            );
        }
        return null;
    };
}

export default MfrmNavigationMenuView;